import { useState, useEffect } from 'react';
import { RefreshCw, Plus, Trash2, Pause, Play, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import { formatCurrency, formatDate, daysUntil } from '@/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/common/Skeleton';
import toast from '@/hooks/useToast';

interface Subscription {
  id: string;
  productId: string;
  quantity: number;
  intervalDays: number;
  isActive: boolean;
  nextOrderDate: string;
  lastOrderDate?: string;
  product: { id: string; name: string; slug: string; images: string[]; price: number; discountedPrice?: number };
}

const INTERVALS = [
  { label: '15 days', value: 15 },
  { label: '30 days', value: 30 },
  { label: '45 days', value: 45 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

export const SubscriptionsPage = () => {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetch = () => {
    setLoading(true);
    api.get('/subscriptions').then(r => setSubs(r.data.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const toggle = async (sub: Subscription) => {
    setUpdating(sub.id);
    try {
      const res = await api.put(`/subscriptions/${sub.id}`, { isActive: !sub.isActive });
      setSubs(p => p.map(s => s.id === sub.id ? res.data.data : s));
      toast.success(sub.isActive ? 'Subscription paused' : 'Subscription resumed');
    } catch { toast.error('Could not update subscription'); }
    finally { setUpdating(null); }
  };

  const cancel = async (id: string) => {
    if (!window.confirm('Cancel this subscription?')) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      setSubs(p => p.filter(s => s.id !== id));
      toast.info('Subscription cancelled');
    } catch { toast.error('Could not cancel'); }
  };

  const updateInterval = async (sub: Subscription, intervalDays: number) => {
    setUpdating(sub.id);
    try {
      const res = await api.put(`/subscriptions/${sub.id}`, { intervalDays });
      setSubs(p => p.map(s => s.id === sub.id ? res.data.data : s));
      toast.success('Interval updated');
    } catch { toast.error('Could not update'); }
    finally { setUpdating(null); }
  };


  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
            <RefreshCw className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl text-gray-800">My Subscriptions</h1>
            <p className="text-sm text-gray-500">Auto-refill your regular medicines</p>
          </div>
        </div>
      </div>

      <div className="bg-primary-50 rounded-xl p-4 mb-6 text-sm text-primary-800">
        <strong>How it works:</strong> Subscribe to any medicine and we'll automatically add it to your cart on schedule. You'll get notified when it's ready to checkout.
      </div>

      {loading ? (
        <div className="space-y-4">{Array(2).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : subs.length === 0 ? (
        <div className="text-center py-16">
          <RefreshCw className="h-16 w-16 text-gray-200 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-600 mb-2">No active subscriptions</h3>
          <p className="text-gray-400 text-sm mb-4">Visit any product page and click "Subscribe" to set up auto-refill.</p>
          <Button asChild><Link to="/products">Browse Products</Link></Button>
        </div>
      ) : (
        <div className="space-y-4">
          {subs.map(sub => {
            const days = daysUntil(sub.nextOrderDate);
            return (
              <div key={sub.id} className={`bg-white rounded-card shadow-card p-5 ${!sub.isActive ? 'opacity-60' : ''}`}>
                <div className="flex gap-4">
                  <img src={sub.product.images[0] || '/placeholder-product.jpg'} alt={sub.product.name} className="w-16 h-16 object-cover rounded-xl shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Link to={`/products/${sub.product.slug}`} className="font-semibold text-gray-800 hover:text-primary-600">{sub.product.name}</Link>
                        <p className="text-sm text-primary-600 font-semibold">{formatCurrency(Number(sub.product.discountedPrice || sub.product.price))} × {sub.quantity}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${sub.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {sub.isActive ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="h-3.5 w-3.5" />
                        {sub.isActive ? (
                          <span>{days === 0 ? 'Due today!' : `Due in ${days} days`} · {formatDate(sub.nextOrderDate)}</span>
                        ) : <span>Paused</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={sub.intervalDays}
                        onChange={e => updateInterval(sub, parseInt(e.target.value))}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-300"
                      >
                        {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                      </select>
                      <button
                        onClick={() => toggle(sub)}
                        disabled={updating === sub.id}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 border border-gray-200 rounded-lg px-2 py-1"
                      >
                        {sub.isActive ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Resume</>}
                      </button>
                      <button onClick={() => cancel(sub.id)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 border border-gray-200 rounded-lg px-2 py-1 ml-auto">
                        <Trash2 className="h-3 w-3" /> Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
